<script lang="ts">
	import { copyLot, validateAndSaveLot, validateLot } from '$lib/map/types/lot'
	import InputField from '$lib/molecules/InputField.svelte'
	import type { FontProps } from '$types/fontProps'
	import { DefaultRobotoFontProps } from '$types/fontProps'
	import { inputFieldFormats } from '$utils/formatters'
	import { CalcMethod, LotType, getLotTypeOptions, UserPermission } from '$types/enums'
	import type { AutocompleteFieldConfig } from '$types/autocompleteFieldConfig'
	import { AutocompleteConfig } from '$types/autocompleteFieldConfig'
	import { t } from '$i18n/i18n'
	import AutocompleteInputField from '$lib/autocomplete/AutocompleteInputField.svelte'
	import UserAccountAutocomplete from '$lib/autocomplete/UserAccountAutocomplete.svelte'
	import PersonAndCompanyDataAutocomplete from '$lib/autocomplete/PersonAndCompanyDataAutocomplete.svelte'
	import DatePickerInput from '$lib/atoms/DatePickerInput.svelte'
	import { tick } from 'svelte'
	import dayjs from 'dayjs'
	import { derived } from 'svelte/store'
	import { copyAddressData, DefaultAddressData } from '$types/addressData'
	import type { LotEditFieldsView } from '$types/lotEditFieldsView'
	import { DefaultLotEditFieldsView } from '$types/lotEditFieldsView'
	import NullableDataWrapper from '$utils/NullableDataWrapper.svelte'
	import { currentLot, currentProject, mapFormControls } from '$lib/map/fak/utils/fakStores'
	import type { PersonAndCompanyData } from '$types/personAndCompanyData'
	import {
		copyPersonAndCompanyData,
		DefaultPersonAndCompanyData
	} from '$types/personAndCompanyData'
	import AddressDataAutocomplete from '$lib/autocomplete/AddressDataAutocomplete.svelte'
	import { hideAutosaveInfo } from '$utils/autosaveInfoUtils'
	import { jwt } from '$utils/jwt'
	import {
		copyUserAccountData,
		DefaultUserAccountData,
		type UserAccountData
	} from '$types/userAccountData'
	import Tags from '$lib/tags/Tags.svelte'
	import Permission from '$lib/menubar/Permission.svelte'

	const LOT_FORM_ERROR_PATH_PREFIX = 'lot.'

	let startDate: number | null
	let endDate: number | null

	let loginUser: UserAccountData = DefaultUserAccountData()
	if ($jwt) {
		jwt.subscribe((claims: Record<string, string>) => {
			loginUser.id = Number(claims.uid)
			loginUser.firstName = claims.ufirstname
			loginUser.lastName = claims.ulastname
		})
	}

	// eslint-disable-next-line no-useless-assignment -- FIXME: svelte 4 reactive reassignment; initial value needed before $: runs
	let lotEditFieldsView: LotEditFieldsView = DefaultLotEditFieldsView()
	$: lotEditFieldsView = $currentLot
		? $currentLot.lotViewFieldsChangeMap
		: DefaultLotEditFieldsView()

	let canNotEditLot = !lotEditFieldsView.canEditLot
	let canNotEditCaseManager = !lotEditFieldsView.canEditCaseManager

	let contactPerson: PersonAndCompanyData | null = null
	let personLink: string | null = null
	let onboardingComment: string | null = null

	$: if ($currentLot) {
		canNotEditLot = !lotEditFieldsView.canEditLot
		canNotEditCaseManager = !lotEditFieldsView.canEditCaseManager
	}

	$: contactEditView = {
		canEditOthers: lotEditFieldsView.canEditLot,
		canEditPhone: lotEditFieldsView.canEditAfterApproval
	}

	$: lotTypeOptions = getLotTypeOptions($currentLot?.type)

	const calcMethodOptions = Object.values(CalcMethod)
		.filter((x) => x !== CalcMethod.COUNTER_OFFER)
		.map(function (x) {
			return { value: x, text: $t('CalcMethod.' + x) }
		})

	const fontProps: FontProps = DefaultRobotoFontProps('0.875rem', '0.75rem')

	let typeAcConfig: AutocompleteFieldConfig = AutocompleteConfig(
		'value',
		'label',
		false,
		!lotEditFieldsView.canEditLot
	)
	let calcMethodAcConfig: AutocompleteFieldConfig = AutocompleteConfig(
		'value',
		'text',
		false,
		!lotEditFieldsView.canEditLot
	)

	let rerenderTrigger: Record<string, never> | null = null

	async function onStartDateChanged() {
		if (!$currentLot) return
		if (startDate) {
			await tick()
			$currentLot.startDate = dayjs.unix(startDate).add(12, 'h').toISOString().split('T')[0]
			triggerLotValidationAndSave()
		} else {
			startDate = $currentLot.startDate ? dayjs($currentLot.startDate, 'YYYY-MM-DD').unix() : null
		}
	}

	async function onEndDateChanged() {
		if (!$currentLot) return
		await tick()
		if (endDate) {
			$currentLot.endDate = dayjs.unix(endDate).add(12, 'h').toISOString().split('T')[0]
		} else {
			$currentLot.endDate = ''
		}
		triggerLotValidationAndSave()
	}

	function triggerLotValidationAndSave() {
		if ($currentLot) {
			if (!contactPerson) {
				$currentLot.person = DefaultPersonAndCompanyData()
			} else {
				$currentLot.person =
					copyPersonAndCompanyData(contactPerson) ?? DefaultPersonAndCompanyData()
			}
			$currentLot.person.link = personLink ?? undefined
			$currentLot.person.onboardingComment = onboardingComment ?? undefined

			if ($currentLot.id !== 'new') {
				validateAndSaveLot(copyLot($currentLot))
			}
		}
	}

	function initializeFieldsAndObjects() {
		if (!$currentLot) return
		if ($currentLot.id === 'new' && $currentProject) {
			$currentLot.project = $currentProject.id
			$currentLot.address = copyAddressData($currentProject.address)
			$currentLot.address.anonymize = true // new lots are always anonymized by default
			$currentLot.person =
				copyPersonAndCompanyData($currentProject.contactPersonAndCompany) ??
				DefaultPersonAndCompanyData()
			$currentLot.person.link = ''
			$currentLot.startDate = $currentProject.startDate
			$currentLot.endDate = $currentProject.endDate
			$currentLot.kickbackInPercent = 5
			$currentLot.kickbackPerTon = 0.5
			$currentLot.caseManager = copyUserAccountData(loginUser)
			fillProjectRemarkField()
		}
		startDate = $currentLot.startDate ? dayjs($currentLot.startDate, 'YYYY-MM-DD').unix() : null
		endDate = $currentLot.endDate ? dayjs($currentLot.endDate, 'YYYY-MM-DD').unix() : null
		rerenderTrigger = {} // reassign the rerenderTrigger to completely rerender the caseOwner Autocomplete-Field
		contactPerson = $currentLot?.person ?? null
		personLink = $currentLot?.person?.link ?? null
		onboardingComment = $currentLot?.person?.onboardingComment ?? null
		if ($currentLot.type === LotType.TRANSPORT && !$currentLot.offerSiteLocation) {
			$currentLot.offerSiteLocation = DefaultAddressData()
		}
	}

	function onCalcMethodChange() {
		if (!$currentLot) return
		fillProjectRemarkField()
		if ($currentLot.calcMethod === CalcMethod.REQUEST_OFFER) {
			$currentLot.grossPricePerTon = undefined
		}
	}

	function onLotTypeChange() {
		if (!$currentLot) return
		fillProjectRemarkField()
		triggerLotValidationAndSave()
		if ($currentLot.type === LotType.TRANSPORT && !$currentLot.offerSiteLocation) {
			$currentLot.offerSiteLocation = DefaultAddressData()
		}
	}

	function fillProjectRemarkField() {
		if (!$currentLot) return
		if ($currentLot.type === LotType.RETURN_FREIGHT) {
			$currentLot.projectRemarks = ''
			return
		}
		let calcMethodStr = $currentLot.calcMethod === CalcMethod.NORMAL ? 'normal' : 'requestOffer'
		let typeStr
		switch ($currentLot.type) {
			case LotType.MATERIAL_SALE:
				typeStr = 'materialSale'
				break
			case LotType.MATERIAL_NEED:
				typeStr = 'materialNeed'
				break
			default:
				typeStr = $currentLot.type.toLowerCase()
		}
		$currentLot.projectRemarks = $t('Model.generalProjectInfo.' + typeStr + '.' + calcMethodStr)
	}

	/* The derived store makes sure that reactivity for field and object initialization is triggered, if the lot.id
	 * has changed, i.e., a different lot is selected. */
	let currentLotId = derived(currentLot, ($currentLot) => $currentLot?.id)
	currentLotId.subscribe(() => {
		if ($currentLot) {
			initializeFieldsAndObjects()
			let lotToValidate = copyLot($currentLot)
			if (!$mapFormControls.saving) {
				hideAutosaveInfo()
			}
			if ($currentLot.id !== 'new') {
				validateLot(lotToValidate)
			}
		}
	})

	initializeFieldsAndObjects()
</script>

<NullableDataWrapper bind:data={$currentLot}>
	{#if $currentLot}
		<div class="fak__lot-form-header">
			<span class="fak__lot-form-card-title">{$t('UI.map.lot.generalInfo')}</span>
			{#if $currentLot.id !== 'new'}
				<a href="/lot/{$currentLot.id}" target="_blank">
					<span class="mm-code-link">
						{$currentLot.code}
					</span>
				</a>
			{/if}
		</div>
		<div class="fak__lot-form-container">
			<div class="fak__lot-form-main-data fak__lot-form-section">
				<div style="grid-area: label">
					<InputField
						id="lot-id"
						bind:value={$currentLot.correlationId}
						{fontProps}
						format={inputFieldFormats.FULL}
						placeholder={$t('UI.map.lot.correlationId.ph')}
						readOnly={!lotEditFieldsView.canEditLot ||
							lotEditFieldsView.isFromSaasPublishedOnPlatform}
						errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'correlationId'}
						on:focusout={triggerLotValidationAndSave}
					/>
				</div>
				<div style="grid-area: caseManager">
					{#key rerenderTrigger}
						<UserAccountAutocomplete
							bind:account={$currentLot.caseManager}
							disabled={canNotEditCaseManager}
							id="lot-case-manager"
							{fontProps}
							label={$t('UI.caseManager.autocomplete.ph')}
							errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'caseManager.*'}
							onChange={triggerLotValidationAndSave}
						/>
					{/key}
					<!-- because case Manager is a nested type, we can show errors for all properties -->
				</div>
				<div style="grid-area: type">
					<AutocompleteInputField
						bind:value={$currentLot.type}
						bind:disabled={canNotEditLot}
						id="lot-type"
						{fontProps}
						autocompleteConfig={typeAcConfig}
						options={lotTypeOptions}
						label={$t('UI.type.autocomplete.ph')}
						errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'type'}
						required
						onChange={() => {
							onLotTypeChange()
						}}
					/>
				</div>
				<div style="grid-area: calcMethod">
					<AutocompleteInputField
						bind:value={$currentLot.calcMethod}
						bind:disabled={canNotEditLot}
						id="lot-calc-method"
						{fontProps}
						autocompleteConfig={calcMethodAcConfig}
						label={$t('UI.calcMethod.autocomplete.ph')}
						options={calcMethodOptions}
						errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'calcMethod'}
						required
						onChange={() => {
							onCalcMethodChange()
							triggerLotValidationAndSave()
						}}
					/>
				</div>
				<div style="grid-area: personContact">
					<PersonAndCompanyDataAutocomplete
						bind:contactPerson
						bind:personLink
						bind:onboardingComment
						bind:isEditable={lotEditFieldsView.canEditLot}
						personInputId="lot-contact-person"
						{fontProps}
						editView={contactEditView}
						outerId={$currentLot.id}
						isNew={$currentLot.id === 'new'}
						errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'person'}
						required
						showCompany
						onChange={triggerLotValidationAndSave}
					/>
					<!-- no need to append '.*' to path here, is already done in component itself + we need the path for the separated subfields -->
				</div>
			</div>
			<Permission requiredPermissions={UserPermission.USE_TAGS}>
				{#key $currentLotId}
					<Tags lotId={$currentLot.id} />
				{/key}
			</Permission>

			<div class="fak__lot-form-data-section">
				<h4>{$t('UI.map.timeframe')}</h4>
				<div class="fak__lot-form-date-row">
					<!-- Future work: Change permission naming (canNotEditCaseManager) -->
					<DatePickerInput
						bind:date={startDate}
						disabled={canNotEditCaseManager}
						id="lot-select-start-date"
						label=""
						{fontProps}
						format={inputFieldFormats.FULL}
						placeholder={$t('UI.startDate.label')}
						errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'startDate'}
						onchange={onStartDateChanged}
					/>
					<DatePickerInput
						bind:date={endDate}
						disabled={canNotEditCaseManager}
						id="lot-select-end-date"
						label=""
						{fontProps}
						format={inputFieldFormats.FULL}
						placeholder={$t('UI.endDate.label')}
						errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'endDate'}
						onchange={onEndDateChanged}
					/>
				</div>
			</div>
			<div class="fak__lot-form-data-section">
				<AddressDataAutocomplete
					bind:addressData={$currentLot.address}
					label={$t(
						$currentLot.type === LotType.TRANSPORT
							? 'Model.label.loadingPoint'
							: 'UI.map.executionPlace'
					)}
					{fontProps}
					errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'address'}
					outerId={$currentLot.id}
					idPrefix="addressData"
					isEditable={$currentLot.lotViewFieldsChangeMap.canEditLot}
					required
					showAnonymizeCheckbox
					onChange={triggerLotValidationAndSave}
				/>
				{#if $currentLot.type === LotType.TRANSPORT && $currentLot.offerSiteLocation}
					<AddressDataAutocomplete
						bind:addressData={$currentLot.offerSiteLocation}
						label={$t('Model.label.unloadingPoint')}
						{fontProps}
						errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'offerSiteLocation'}
						outerId={$currentLot.id}
						idPrefix="offerSiteLocation"
						isEditable={$currentLot.lotViewFieldsChangeMap.canEditLot}
						required
						showAnonymizeCheckbox
						onChange={triggerLotValidationAndSave}
					/>
				{/if}
			</div>
		</div>
	{/if}
</NullableDataWrapper>

<style lang="scss">
	@import '$styles/global';

	.fak__lot-form-container {
		padding: 0 1.313rem 1.625rem;
		width: 100%;
		max-width: 450px;

		@include flex-col(0.938rem, $justify: flex-start);

		& > div {
			width: 100%;
		}
	}

	.fak__lot-form-card-title {
		@include roboto-font(1rem, 400, 1rem);
	}

	.fak__lot-form-header {
		@include flex-row($justify: space-between, $alignment: center);

		width: 100%;
		padding: 1.625rem 1.31rem 1.25rem;
	}

	.fak__lot-form-main-data {
		width: 100%;
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 0.938rem 0.625rem;
		grid-template-areas:
			'label caseManager'
			'type type'
			'calcMethod calcMethod'
			'personContact personContact';
	}

	.mm-code-link {
		float: right;

		@include roboto-font(normal, 400, 0.75rem, $primaryGreen);

		&:hover {
			text-decoration: underline;
		}
	}

	.fak__lot-form-data-section {
		width: 100%;

		@include flex-col(0.75rem, $alignment: flex-start);
	}

	.fak__lot-form-date-row {
		width: 100%;

		@include flex-row(0.625rem, $justify: flex-start, $alignment: flex-start);
	}

	.duplicate-address-icon {
		background-color: white;
		background-size: contain;
		position: relative;
		display: inline-block;
		text-align: center;
		width: 24px;
		height: 24px;
		margin-top: 12px;
		margin-right: 6px;

		&:hover {
			cursor: pointer;
		}
	}

	h4 {
		margin: 0;
	}
</style>
